import React, { useEffect, useState } from "react";
import { FacebookProvider } from "react-facebook";
import { Container, Row, Col, Card, CardBody, Button } from "reactstrap";
import { useIntl } from "react-intl";
import { WHATSAPP_FACEBOOK_APP_ID, WhatsAppConnectionMode } from "../../cores/whatsapp/config";
import { WhatsappIcon } from "../component/social/Whatsapp/WhatsappIcon";
import { launchWhatsAppSignup } from "../../cores/whatsapp/services/whatsapp-auth0-config";

export const WhatsappAuth = () => {
  const intl = useIntl();
  const [callbackUrl, setCallbackUrl] = useState<string | null>(null);
  const [isFromMobileApp, setIsFromMobileApp] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  useEffect(() => {
    // Detectar si viene de la app móvil mediante query params
    const urlParams = new URLSearchParams(window.location.search);
    const callback = urlParams.get("callback_url");
    const company = urlParams.get("company_id");

    if (callback) {
      setCallbackUrl(decodeURIComponent(callback));
      setIsFromMobileApp(true);
      console.log(
        "📱 [Web] Sesión iniciada desde app móvil, callback:",
        callback
      );
    }

    if (company) {
      setCompanyId(company);
      console.log("🏢 [Web] Company ID detectado:", company);
    }

    // Detectar si hay parámetros de estado de WhatsApp OAuth (por si viene de un redirect)
    const status = urlParams.get("status");
    const accessToken = urlParams.get("access_token");

    if (status && callback) {
      console.log(
        "📱 [Web] Estado detectado:",
        status,
        accessToken ? "Con token" : "Sin token"
      );

      if (status === "success" && accessToken && company) {
        // Auto-procesar el token de WhatsApp y redirigir
        setTimeout(() => {
          try {
            const url = new URL(decodeURIComponent(callback));
            url.searchParams.set("status", "success");
            url.searchParams.set("access_token", accessToken);
            url.searchParams.set("company_id", company);
            window.location.href = url.toString();
          } catch (error) {
            console.error("📱 [Web] Error en auto-redirect:", error);
          }
        }, 2000);
      }
    }
  }, []);

  const handleWhatsappAuth = async (mode: WhatsAppConnectionMode = "coexistence") => {
    if (!companyId) {
      alert("Error: Company ID es requerido para la autenticación");
      return;
    }

    setIsAuthenticating(true);

    try {
      launchWhatsAppSignup(mode, ({ authResponse }: any) => {
        console.log("📱 [Web] Respuesta de WhatsApp OAuth:", authResponse);

        const oauthCode = authResponse?.code || authResponse?.accessToken;

        if (oauthCode) {
          // Si viene de la app móvil, construir deep link manualmente
          if (isFromMobileApp && callbackUrl) {
            console.log(
              "📱 [Web] Construyendo deep link para regresar a la app móvil..."
            );

            // Crear el deep link con los datos de autenticación
            const deepLinkUrl = `wembii://whatsapp/callback?status=success&access_token=${encodeURIComponent(
              oauthCode
            )}&company_id=${companyId}`;

            // También crear universal link como fallback
            const universalLinkUrl = `https://app.wembii.com/app/whatsapp-callback?status=success&access_token=${encodeURIComponent(
              oauthCode
            )}&company_id=${companyId}`;

            console.log("📱 [Web] Deep link generado:", deepLinkUrl);
            console.log("📱 [Web] Universal link generado:", universalLinkUrl);

            // Mostrar mensaje antes de redirigir
            alert("WhatsApp conectado exitosamente! Regresando a la app...");

            // Redirigir después de un pequeño delay
            setTimeout(() => {
              try {
                // Intentar primero con el deep link personalizado
                window.location.href = deepLinkUrl;

                // Fallback: si no funciona, intentar con universal link después de un tiempo
                setTimeout(() => {
                  if (document.hasFocus()) {
                    // Si aún estamos en la web, intentar universal link
                    console.log("📱 [Web] Intentando con universal link...");
                    window.location.href = universalLinkUrl;
                  }
                }, 2000);
              } catch (error) {
                console.error("📱 [Web] Error redirigiendo a la app:", error);
                alert(
                  "Error al regresar a la app. Por favor, abre la app Wembii manualmente."
                );
              }
            }, 1500);
          } else {
            // Flujo normal para web o iframe
            // Notificar a la aplicación padre si está en un iframe
            if (window.parent && window.parent !== window) {
              window.parent.postMessage(
                {
                  type: "WHATSAPP_AUTH_SUCCESS",
                  data: {
                    accessToken: oauthCode,
                    companyId: companyId,
                  },
                },
                "*"
              );
            }

            // Mostrar mensaje de éxito
            alert("WhatsApp conectado exitosamente!");
          }
        } else {
          console.log(
            "📱 [Web] Usuario canceló la autenticación o no autorizó completamente."
          );

          if (isFromMobileApp && callbackUrl) {
            // Redirigir con estado de cancelación
            const url = new URL(decodeURIComponent(callbackUrl));
            url.searchParams.set("status", "cancelled");
            window.location.href = url.toString();
          }
        }

        setIsAuthenticating(false);
      });
    } catch (error) {
      console.error("📱 [Web] Error en autenticación de WhatsApp:", error);
      setIsAuthenticating(false);

      if (isFromMobileApp && callbackUrl) {
        // Redirigir con estado de error
        const url = new URL(decodeURIComponent(callbackUrl));
        url.searchParams.set("status", "error");
        url.searchParams.set("error", "authentication_failed");
        window.location.href = url.toString();
      } else {
        alert(
          "Error en la autenticación de WhatsApp. Por favor, inténtalo de nuevo."
        );
      }
    }
  };

  const authCard = (
    <Container
      className="h-100 d-flex align-items-center justify-content-center"
      fluid
    >
      <Row className="w-100">
        <Col md={{ offset: 3, size: 6 }} sm="12">
          <Card className="shadow-lg">
            <CardBody className="text-center p-5">
              {/* Indicador de sesión desde app móvil */}
              {isFromMobileApp && (
                <div className="alert alert-info mb-4">
                  <i className="fas fa-mobile-alt me-2"></i>
                  <strong>Sesión desde App Móvil</strong>
                  <br />
                  <small>
                    Cuando completes la autenticación, regresarás
                    automáticamente a la app Wembii
                  </small>
                </div>
              )}

              <div className="mb-4">
                <div className="mb-3">
                  <WhatsappIcon size="6x" active={false} />
                </div>
                <h2 className="text-primary mb-3">
                  {intl.formatMessage({ id: "app.whatsappAuth.title" })}
                </h2>
                <p className="text-muted">
                  {intl.formatMessage({ id: "app.whatsappAuth.subtitle" })}
                </p>
              </div>

              <div className="mb-4 d-flex flex-column gap-2">
                <Button
                  color="success"
                  size="lg"
                  onClick={() => handleWhatsappAuth("coexistence")}
                  disabled={isAuthenticating || !companyId}
                  className="px-4"
                >
                  {isAuthenticating ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-2"></i>
                      {intl.formatMessage({
                        id: "app.whatsappAuth.authenticating",
                      })}
                    </>
                  ) : (
                    <>
                      <i className="fab fa-whatsapp me-2"></i>
                      {intl.formatMessage({
                        id: "app.whatsappConnect.existingBusiness",
                      })}
                    </>
                  )}
                </Button>
                <Button
                  color="outline-success"
                  size="lg"
                  onClick={() => handleWhatsappAuth("api_only")}
                  disabled={isAuthenticating || !companyId}
                  className="px-4"
                >
                  {intl.formatMessage({ id: "app.whatsappConnect.newNumber" })}
                </Button>
              </div>

              {!companyId && (
                <div className="alert alert-warning">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {intl.formatMessage({
                    id: "app.whatsappAuth.companyRequired",
                  })}
                </div>
              )}

              <div className="mt-4">
                <small className="text-muted">
                  {intl.formatMessage({ id: "app.whatsappAuth.secureAuth" })}
                </small>
              </div>

              {/* Botón para regresar manualmente a la app móvil */}
              {isFromMobileApp && callbackUrl && (
                <div className="mt-4">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      if (
                        window.confirm(
                          "¿Quieres regresar a la app sin conectar WhatsApp?"
                        )
                      ) {
                        const url = new URL(decodeURIComponent(callbackUrl));
                        url.searchParams.set("status", "cancelled");
                        window.location.href = url.toString();
                      }
                    }}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    Regresar a la App
                  </button>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );

  if (!WHATSAPP_FACEBOOK_APP_ID) {
    return authCard;
  }

  return (
    <FacebookProvider version="v22.0" appId={WHATSAPP_FACEBOOK_APP_ID}>
      {authCard}
    </FacebookProvider>
  );
};
